import { useState, useEffect, useCallback } from "react";
import {
  UserEntity,
  LoginCredentials,
  LoginUseCase,
  LogoutUseCase,
  GetCurrentUserUseCase,
} from "../../domain";
import { AuthRepositoryImpl } from "../../data/repositories/AuthRepositoryImpl";

// Create repository instance (singleton)
const authRepository = new AuthRepositoryImpl();

// Create use cases
const loginUseCase = new LoginUseCase(authRepository);
const logoutUseCase = new LogoutUseCase(authRepository);
const getCurrentUserUseCase = new GetCurrentUserUseCase(authRepository);

export interface UseAuthReturn {
  user: UserEntity | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<{ success: boolean }>;
  logout: () => Promise<void>;
  isMasterAdmin: () => boolean;
  needsPasswordChange: () => boolean;
  clearError: () => void;
}

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<UserEntity | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = () => {
      try {
        const authenticated = getCurrentUserUseCase.isAuthenticated();
        const storedUser = getCurrentUserUseCase.getCurrentUser();

        if (authenticated && storedUser) {
          setUser(storedUser);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        authRepository.logout();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  /**
   * Login
   */
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<{ success: boolean }> => {
      setLoading(true);
      setError(null);

      try {
        const result = await loginUseCase.execute(credentials);
        setUser(result.user);
        setIsAuthenticated(true);
        return { success: true };
      } catch (error: any) {
        const errorMessage = error.message || "Login failed. Please try again.";
        setError(errorMessage);
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Logout
   */
  const logout = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      await logoutUseCase.execute();
    } catch (error) {
      console.warn("Logout error:", error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
      setLoading(false);
    }
  }, []);

  /**
   * Check if user is master admin
   */
  const isMasterAdmin = useCallback((): boolean => {
    return getCurrentUserUseCase.isMasterAdmin();
  }, []);

  /**
   * Check if user needs to change password
   */
  const needsPasswordChange = useCallback((): boolean => {
    if (!user) return false;
    // Master admin (admin@zournalhq.com) doesn't need password change
    if (isMasterAdmin() && user.email === "admin@zournalhq.com") {
      return false;
    }
    // If password_changed_at is null, user needs to change password
    return (
      user.password_changed_at === null ||
      user.password_changed_at === undefined
    );
  }, [user, isMasterAdmin]);

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    user,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    isMasterAdmin,
    needsPasswordChange,
    clearError,
  };
};
